import { useEffect, useRef, useState } from "react";
import toast from "../../../utils/toast";
import store from "../../../store";
import SDClient from "../../../sd_core/client";
import { v4 as uuidv4 } from "uuid";
import {
  validateUserDetails,
  validateShippingAddress,
  validateBillingAddress,
} from "../../../utils/validators";
import useRazorpay from "react-razorpay";

window.SD = SDClient;

function useCheckout({ cartItems, profile_data, extendedItems, history }) {
  const [userDetails, setUserDetails] = useState({});
  const [shippingDetails, setShippingDetails] = useState({
    address_country: "India",
    address_zipcode: "",
    address_state: "",
    address_city: "",
    address_address: "",
  });
  const [billingDetails, setBillingDetails] = useState({
    address_country: "India",
    address_zipcode: "",
    address_state: "",
    address_city: "",
    address_address: "",
  });

  const [message, setMessage] = useState({ description: "" });

  const [loadingVO, setLoadingVO] = useState(true);
  const [VOData, setVOData] = useState({});

  const [BillingShippingAddressSame, setBillingShippingAddressSame] = useState(
    true,
  );
  const BillingAddressBlockRef = useRef(null);

  const [inTransition, setInTransition] = useState(false);
  const [SelectedPaymentConfirmor, setSelectedPaymentConfirmor] = useState(
    null,
  );

  const Razorpay = useRazorpay();
  const [Order, setOrder] = useState(null);
  const [OrderPayment, setOrderPayment] = useState(null);
  const [OrderResponse, setOrderResponse] = useState(null);
  const [remoteValidationTrace, setRemoteValidationTrace] = useState({
    user: {},
    shipping_address: {},
    billing_address: {},
  });

  const getAvailablePaymentMethods = (virtualOrderData) => {
    if (
      !virtualOrderData ||
      !virtualOrderData.options ||
      !virtualOrderData.options.payment_methods
    ) {
      return [];
    }

    return Object.values(virtualOrderData.options.payment_methods);
  };

  const getValidAppliedPaymentMethod = (virtualOrderData) => {
    const appliedPaymentMethod =
      virtualOrderData &&
      virtualOrderData.order &&
      virtualOrderData.order.applied_payment_method;

    if (
      !appliedPaymentMethod ||
      !appliedPaymentMethod.virtual_order_payment_mode
    ) {
      return null;
    }

    const isAvailable = getAvailablePaymentMethods(virtualOrderData).some(
      (method) => method.id === appliedPaymentMethod.virtual_order_payment_mode,
    );

    return isAvailable ? appliedPaymentMethod : null;
  };

  const syncCart = (callback) => {
    let cart = [];
    cartItems.map((item, i) => {
      cart.push({
        sku: item.sku,
        quantity: item.qty,
        variant: item.selectedVariant.id,
      });
    });
    store.dispatch({
      type: "READY_UPDATE_TO_SERVER",
    });
    // Guests must NOT send user_id — their cart is keyed by the session so
    // it can be merged into the user on login.
    const userId = localStorage.getItem("user_id");
    const cartUpdatePayload = userId
      ? { user_id: userId, cart: cart }
      : { cart: cart };
    SDClient.endpoint("cart_update", cartUpdatePayload).then(
      (data) => {
        if (data.message.action) {
          store.dispatch({
            type: "UPDATE_TO_SERVER",
            payload: { cart: data.message.cart },
          });
          callback();
        } else {
          toast.error("Something Went Wrong, please try again.", {
            toastId: "checkout-sync-error",
          });
          return;
        }
      },
      (error) => {
        if (error.reason == "CLIENT_ERROR") {
          toast.warn(
            "Oops! Unable to checkout, please make sure to enable cookies or try clearing your browser cache.",
            {
              toastId: "checkout-cookies-error",

              hideProgressBar: true,
            },
          );
          history.push("/cart");
        }
      },
    );
  };

  const updateVO = (callback = null) => {
    _updateVO(callback);
  };

  const _updateVO = (callback = null) => {
    setLoadingVO(true);
    SDClient.endpoint("virtual_order").then(
      (response) => {
        setVOData(response.message);
        setLoadingVO(false);
        if (callback) callback(response);
      },
      (error) => {
        if (callback) callback(error);
      },
    );
  };

  const gaInitiateCheckout = () => {
    const products = [];
    for (let i = 0; i < cartItems.length; i++) {
      products.push({
        name: cartItems[i].name,
        id: cartItems[i].sku,
        price:
          cartItems[i].selectedVariant &&
          cartItems[i].selectedVariant.offerprice,
        brand: "Sunny Diamonds",
        category: cartItems[i].value,
        variants:
          cartItems[i].selectedVariant && cartItems[i].selectedVariant.color,
        quantity: cartItems[i].qty,
      });
    }
    window.dataLayer = window.dataLayer || [];
    try {
      const eventLabel = `InitiateCheckout_${uuidv4()}`;
      window.dataLayer.push({
        event: "InitiateCheckout",
        ecommerce: {
          currencyCode: "INR",
          impressions: products,
        },
        eventLabel: eventLabel,
      });
    } catch (err) {
      if (
        window.dataLayer.filter(function(obj) {
          return obj.errorMsg === err.message;
        }).length == 0
      ) {
        window.dataLayer.push({
          event: "variable error",
          errorMsg: err.message,
        });
      }
    }
  };

  useEffect(() => {
    if (OrderResponse) return;
    gaInitiateCheckout();

    if (cartItems.length) {
      syncCart(updateVO);
    } else {
      history.push("/cart");
    }
  }, [cartItems]);

  useEffect(() => {
    if (VOData.order) {
      if (
        VOData.order.applied_payment_method &&
        !getValidAppliedPaymentMethod(VOData)
      ) {
        setSelectedPaymentConfirmor(null);
        setOrder(null);
        setOrderPayment(null);
      }
      if (VOData.order.billing_address && VOData.order.shipping_address) {
        // setBillingShippingAddressSame(checkAddressEquality(VOData.order.billing_address, VOData.order.shipping_address));
        // setBillingShippingAddressSame(true);
      }
    } else if (VOData.message && VOData.message === "Empty/Invalid Cart") {
      history.push("/cart");
    }
  }, [VOData]);

  useEffect(() => {
    if (window.innerWidth > 959 && BillingAddressBlockRef.current)
      BillingAddressBlockRef.current.scrollIntoView({
        block: "end",
        inline: "nearest",
        behavior: "smooth",
      });
  }, [BillingShippingAddressSame]);

  const validateCheckoutFn = (sections) => {
    const errors = { user: {}, shipping_address: {}, billing_address: {} };
    let hasErrors = false;

    if (!sections || sections.user) {
      const userErrors = validateUserDetails(userDetails);
      if (Object.keys(userErrors).length > 0) {
        errors.user = userErrors;
        hasErrors = true;
      }
    }

    if (!sections || sections.shipping) {
      const shippingErrors = validateShippingAddress(shippingDetails);
      if (Object.keys(shippingErrors).length > 0) {
        errors.shipping_address = shippingErrors;
        hasErrors = true;
      }
    }

    if (!sections || sections.billing) {
      if (!BillingShippingAddressSame) {
        const billingErrors = validateBillingAddress(billingDetails);
        if (Object.keys(billingErrors).length > 0) {
          errors.billing_address = billingErrors;
          hasErrors = true;
        }
      }
    }

    return { errors: errors, hasErrors: hasErrors };
  };

  const submitCheckout = () => {
    const result = validateCheckoutFn();
    if (result.hasErrors) {
      setRemoteValidationTrace(result.errors);
      toast.warn("Please fill in all required fields correctly.", {
        toastId: "checkout-validation-error",
        hideProgressBar: true,
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
      return "address_error";
    }

    if (!VOData.order || !getValidAppliedPaymentMethod(VOData)) {
      return "payment_error";
    }

    setRemoteValidationTrace({
      user: {},
      shipping_address: {},
      billing_address: {},
    });
    setLoadingVO(true);

    const saveUserDetails = SDClient.endpoint(
      "virtual_order_set_user_details",
      {
        ...userDetails,
      },
    );
    const saveShipping = SDClient.endpoint(
      "virtual_order_set_shipping_address",
      {
        ...shippingDetails,
      },
    );
    const saveBilling = !BillingShippingAddressSame
      ? SDClient.endpoint("virtual_order_set_billing_address", {
          ...billingDetails,
        })
      : Promise.resolve({ message: { action: true } });

    Promise.all([saveUserDetails, saveShipping, saveBilling]).then(
      function([userRes, shippingRes, billingRes]) {
        const allSucceeded =
          userRes.message.action &&
          shippingRes.message.action &&
          billingRes.message.action;

        if (!allSucceeded) {
          setLoadingVO(false);
          if (
            !shippingRes.message.action &&
            shippingRes.message.reason === "SHIPPING_PINCODE_ERROR"
          ) {
            toast.error(
              "Sorry, we are unable to deliver to this pincode. Please try a different shipping address.",
              {
                toastId: "checkout-shipping-pincode-error",

                hideProgressBar: true,
              },
            );
          } else {
            toast.error(
              "Failed to save checkout details. Please review your information and try again.",
              {
                toastId: "checkout-save-details-failed",

                hideProgressBar: true,
              },
            );
          }
          return;
        }
        updateVO(orderPlace);
      },
      function() {
        setLoadingVO(false);
        toast.error(
          "Unable to connect to the server. Please check your connection and try again.",
          {
            toastId: "checkout-submit-error",
            hideProgressBar: true,
          },
        );
      },
    );
    return true;
  };

  const orderPlace = (response) => {
    const payment_method = getValidAppliedPaymentMethod(response.message);
    if (payment_method == null) {
      toast.warn(
        "Oops! No payment method selected, please choose a payment method to continue.",
        {
          toastId: "checkout-no-payment-method",
          hideProgressBar: true,
        },
      );
      setSelectedPaymentConfirmor(null);
      setOrder(null);
      setOrderPayment(null);
    } else {
      generate_order();
    }
  };

  const generate_order = () => {
    console.log("hii");
    setOrder(null);
    setInTransition(true);
    SDClient.endpoint("order_generate", {
      user_id: profile_data
        ? profile_data.user_id
        : localStorage.getItem("user_id"),
    }).then((data) => {
      setInTransition(false);
      if (data.message.action) {
        let order_data = data.message.order;
        order_data.items = data.message.items;
        let payment_initiation = data.message.payment_data;
        order_data.payment_data = payment_initiation;

        // Fully covered by gift card — order is already created & confirmed
        // server-side. Skip Razorpay and order_confirm; go straight to success.
        if (payment_initiation && payment_initiation.fully_paid) {
          setOrderResponse(data.message);
          setTimeout(() => {
            window.scrollTo({ top: 0, behavior: "smooth" });
          }, 500);
          store.dispatch({ type: "REMOVE_ALL_CART" });
          return;
        }

        setOrder(order_data);
        setOrderPayment(payment_initiation);
        setSelectedPaymentConfirmor(order_data.payment_mode);

        const products = [];
        for (let i = 0; i < order_data.items.length; i++) {
          products.push({
            name: order_data.items[i].name,
            id: order_data.items[i].sku,
            price: order_data.items[i].total,
            brand: "Sunny Diamonds",
            category: order_data.items[i].sku,
            variant: order_data.items[i].color,
            quantity: order_data.items[i].qty,
          });
        }
        window.dataLayer = window.dataLayer || [];
        try {
          window.dataLayer.push({
            event: "checkout",
            ecommerce: {
              checkout: {
                actionField: { step: 4, option: order_data.payment_mode },
                products: [products],
              },
            },
          });
        } catch (err) {
          if (
            window.dataLayer.filter(function(obj) {
              return obj.errorMsg === err.message;
            }).length == 0
          ) {
            window.dataLayer.push({
              event: "variable error",
              errorMsg: err.message,
            });
          }
        }
      } else {
        if (data.message.reason == "SYSTEM_RUNTIME_EXCEPTION") {
          if (data.message.trace.error == "INCOMPLETE_ORDER_USER") {
            toast.warn(
              "Oops! Please complete your user details and proceed to checkout.",
              {
                toastId: "checkout-user-details-missing",

                hideProgressBar: true,
              },
            );
            setRemoteValidationTrace({
              ...remoteValidationTrace,
              user: {
                first_name: true,
                last_name: true,
                email: true,
                phone: true,
              },
            });
            window.scrollTo({ top: 0, behavior: "smooth" });
          } else if (
            data.message.trace.error == "INCOMPLETE_ORDER_SHIPPING_ADDRESS"
          ) {
            toast.warn(
              "Oops! Please add your shipping addresss and proceed to checkout.",
              {
                toastId: "checkout-shipping-address-missing",

                hideProgressBar: true,
              },
            );
            setRemoteValidationTrace({
              ...remoteValidationTrace,
              shipping_address: {
                address_country: true,
                address_zipcode: true,
                address_state: true,
                address_city: true,
                address_address: true,
              },
            });
            window.scrollTo({ top: 0, behavior: "smooth" });
          } else if (data.message.trace.error == "INCOMPLETE_ORDER_CART") {
          } else if (data.message.trace.error == "INCOMPLETE_ORDER_PAYMENT") {
            toast.warn("Please select a payment method to continue.", {
              toastId: "checkout-no-payment-method",

              hideProgressBar: true,
            });
          } else if (data.message.reason == "GIFT_CARD_ALREADY_LOCK_ERROR") {
            toast.warn(
              "Oops! This Gift Card cannot be used, the card is already locked against another order.",
              {
                toastId: "checkout-giftcard-already-locked",

                hideProgressBar: true,
              },
            );
          } else if (data.message.reason == "GIFT_CARD_LOCK_ERROR") {
            toast.warn(
              "Failed to lock the gift card against this order. Please try again or contact customer support.",
              {
                toastId: "checkout-giftcard-lock-error",

                hideProgressBar: true,
              },
            );
          } else {
            toast.error(
              "Order processing failed. Please try again after a while.",
              {
                toastId: "checkout-generic-error",

                hideProgressBar: true,
              },
            );
          }
        } else {
          toast.error(
            "Unable to process your order. Please enable cookies or clear your browser cache and try again.",
            {
              toastId: "checkout-cookies-generic-error",

              hideProgressBar: true,
            },
          );
        }
      }
    });
  };

  const clear_order = () => {
    setSelectedPaymentConfirmor(null);
  };

  const confirm_order = (confirmation, callback) => {
    if (!callback) callback = function() {};
    setInTransition(true);
    const isCOD =
      confirmation.forceCOD ||
      Order.payment_mode === "cod" ||
      Order.payment_mode === "COD";
    const endpoint = isCOD ? "order_confirm_cod" : "order_confirm";
    const payload = isCOD
      ? { otp: confirmation.otp, order_id: Order.order_id, vo_id: Order.vo_id }
      : {
          confirmation: confirmation,
          order_id: Order.order_id,
          vo_id: Order.vo_id,
        };
    const requestOptions = isCOD ? { skipAuth: true } : {};
    SDClient.endpoint(endpoint, payload, requestOptions).then((data) => {
      setInTransition(false);
      if (data.message.action) {
        setSelectedPaymentConfirmor(null);
        const products = [];
        for (let i = 0; i < Order.items.length; i++) {
          products.push({
            name: Order.items[i].name,
            id: Order.items[i].sku,
            price: Order.items[i].total,
            brand: "Sunny Diamonds",
            category: Order.items[i].sku,
            variant: Order.items[i].color,
            quantity: Order.items[i].qty,
            coupon: "",
          });
        }
        window.dataLayer = window.dataLayer || [];
        try {
          window.dataLayer.push({
            event: "transaction",
            ecommerce: {
              purchase: {
                actionField: {
                  id: data.message.order.order_id,
                  affiliation: "Sunny Diamonds Online Store",
                  revenue: data.message.order.amount,
                  tax: "0.00",
                  shipping: "0.00",
                  discount: data.message.order.discount,
                },
                products: products,
              },
            },
          });
        } catch (err) {
          if (
            window.dataLayer.filter(function(obj) {
              return obj.errorMsg === err.message;
            }).length == 0
          ) {
            window.dataLayer.push({
              event: "variable error",
              errorMsg: err.message,
            });
          }
        }

        setOrderResponse(data.message);
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }, 500);
        store.dispatch({ type: "REMOVE_ALL_CART" });
      } else {
        callback(data.message);
      }
    });
  };

  const razorpayConfirm = () => {
    const options = {
      key: global.rzpKey,
      amount: Order.amount * 100,
      currency: Order.currency,
      order_id: Order.order_id,
      name: "Sunny Diamonds",
      description: "Sunny Diamonds Payment",
      image:
        "https://sunnydiamonds.com/assets/images/sunny/logo/razorpay/logo.webp",
      redirect: false,
      handler: function(response) {
        clear_order();
        confirm_order(response, (data) => {
          toast.error(
            "Payment signature verification failed, please try again.",
            { toastId: "checkout-payment-signature-failed" },
          );
        });
      },
      modal: {
        confirm_close: true,
        ondismiss: function() {
          clear_order();
          updateVO();
        },
      },
      prefill: {
        name: Order.first_name + " " + Order.last_name,
        email: Order.email,
        contact: Order.phone,
      },
      notes: { address: "Razorpay Corporate Office" },
      theme: { color: "#722257" },
      timeout: 300,
    };
    const rzpay = new Razorpay(options);
    rzpay.open();
  };

  return {
    // State
    userDetails,
    setUserDetails,
    shippingDetails,
    setShippingDetails,
    billingDetails,
    setBillingDetails,
    message,
    setMessage,
    loadingVO,
    VOData,
    BillingShippingAddressSame,
    setBillingShippingAddressSame,
    inTransition,
    SelectedPaymentConfirmor,
    Order,
    OrderPayment,
    OrderResponse,
    remoteValidationTrace,
    setRemoteValidationTrace,

    // Refs
    BillingAddressBlockRef,

    // Functions
    syncCart,
    updateVO,
    submitCheckout,
    generate_order,
    confirm_order,
    clear_order,
    razorpayConfirm,
  };
}

export default useCheckout;
